import React, { useState, useEffect, useCallback } from 'react';
import { FiGlobe } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import BrandingSection from './BrandingSection';
import ContentSection from './ContentSection';
import GallerySection from './GallerySection';
import FooterSection from './FooterSection';
import FacultySection from './FacultySection';
import CourseSection from './CourseSection';
import StatusNotification from './StatusNotification';
import TabNavigation from './common/TabNavigation';

// Section imports
import HeroSection from './sections/HeroSection';
import StatisticsSection from './sections/StatisticsSection';
import AccreditationsSection from './sections/AccreditationsSection';
import InfrastructureSection from './sections/InfrastructureSection';
import TestimonialsSection from './sections/TestimonialsSection';
import CollaborationsSection from './sections/CollaborationsSection';
import AwardsSection from './sections/AwardsSection';
import LeadershipSection from './sections/LeadershipSection';
import RecruitersSection from './sections/RecruitersSection';
import EventsSection from './sections/EventsSection';
import NewsSection from './sections/NewsSection';
import FAQSection from './sections/FAQSection';
import SEOSection from './sections/SEOSection';

const WebsiteManager = () => {
  const location = useLocation();
  const { tenantId } = location.state || {};
  const token = localStorage.getItem('token') || '';
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('branding');
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState(null);
  const [isNewWebsite, setIsNewWebsite] = useState(false);
  const [websiteVisibility, setWebsiteVisibility] = useState(true);
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);

  const defaultWebsiteData = React.useMemo(() => ({
    branding: { logo: '', title: '', slogan: '', favicon: '', metaDescription: '', primaryColor: '#1a1a2e', secondaryColor: '#c9a84c', fontFamily: 'Inter' },
    hero: { isActive: true, title: '', subtitle: '', description: '', badge: 'Excellence in Education', backgroundImage: '', ctaText: 'Explore Programs', ctaLink: '/programs', secondaryCtaText: 'Contact Admissions', secondaryCtaLink: '/contact', displayStyle: 'default' },
    statistics: { isActive: false, sectionTitle: 'Our Impact by Numbers', sectionSubtitle: 'Transforming lives through education', displayStyle: 'grid-4', animateCounters: true, items: [] },
    accreditations: { isActive: false, sectionTitle: 'Accreditations & Recognitions', sectionSubtitle: 'Validated by prestigious bodies', displayStyle: 'grid', showGrades: true, showDescriptions: true, items: [] },
    infrastructure: { isActive: false, sectionTitle: 'Campus Infrastructure', sectionSubtitle: 'State-of-the-art facilities', displayStyle: 'grid', showFeatures: true, showCaptions: true, categories: [], items: [] },
    testimonials: { isActive: false, sectionTitle: 'Student Voices', sectionSubtitle: 'Real stories from our students', displayStyle: 'featured-grid', showRatings: true, showBatch: true, showCompany: true, showLocation: true, allowVideoTestimonials: false, items: [] },
    collaborations: { isActive: false, sectionTitle: 'Global Partnerships', sectionSubtitle: 'Collaborating with world-class institutions', displayStyle: 'grid', showCountries: true, showFlags: true, showTypes: true, showSince: true, items: [] },
    awards: { isActive: false, sectionTitle: 'Awards & Recognitions', sectionSubtitle: 'Accolades that speak to our excellence', displayStyle: 'grid', showBadges: true, showYears: true, items: [] },
    leadership: { isActive: false, sectionTitle: 'Our Leadership', sectionSubtitle: 'Visionaries driving excellence', displayStyle: 'grid', showSocialLinks: true, showResearchInterests: true, items: [] },
    recruiters: { isActive: false, sectionTitle: 'Our Recruiters', sectionSubtitle: 'Trusted by leading organizations', displayStyle: 'logo-wall', showHires: true, showCategories: true, items: [] },
    events: { isActive: false, sectionTitle: 'Upcoming Events', sectionSubtitle: 'Join us for these exciting events', displayStyle: 'card', showSpeakers: true, showVenue: true, showTime: true, items: [] },
    news: { isActive: false, sectionTitle: 'In the News', sectionSubtitle: 'Making headlines', displayStyle: 'grid', showDates: true, showPublicationLogos: true, items: [] },
    faq: { isActive: false, sectionTitle: 'Frequently Asked Questions', sectionSubtitle: 'Find answers to common questions', displayStyle: 'accordion', showCategories: true, showSearch: false, items: [] },
    gallery: { isActive: false, sectionTitle: 'Campus Life', sectionSubtitle: 'A glimpse into our vibrant community', layout: 'grid', columns: 4, images: [] },
    footer: { description: '', copyrightText: '', showBackToTop: true, socialMedia: { facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '' }, contactInfo: { email: '', phone: '', address: '', mapEmbedUrl: '' }, quickLinks: [] },
    seo: { metaTitle: '', metaDescription: '', keywords: '', canonicalUrl: '' },
    isActive: true,
    sectionOrder: ['hero', 'statistics', 'accreditations', 'programs', 'infrastructure', 'faculty', 'testimonials', 'gallery', 'collaborations', 'awards', 'leadership', 'recruiters', 'events', 'news', 'faq']
  }), []);

  const [websiteData, setWebsiteData] = useState(defaultWebsiteData);

  const fetchWebsiteData = useCallback(async () => {
    if (!tenantId) {
      setError('Tenant ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/website/${tenantId}/data`);
      
      if (response.data.success && response.data.data) {
        const deepMerge = (target, source) => {
          for (const key in source) {
            if (source[key] instanceof Object && !Array.isArray(source[key])) {
              target[key] = deepMerge(target[key] || {}, source[key]);
            } else {
              target[key] = source[key];
            }
          }
          return target;
        };

        const mergedData = deepMerge(JSON.parse(JSON.stringify(defaultWebsiteData)), response.data.data);
        setWebsiteData(mergedData);
        setWebsiteVisibility(mergedData.isActive !== false);
        setIsNewWebsite(false);
      } else {
        setIsNewWebsite(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setIsNewWebsite(true);
      } else {
        setError(error.response?.data?.message || 'Failed to load website data');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, defaultWebsiteData]);

  useEffect(() => {
    fetchWebsiteData();
  }, [fetchWebsiteData]);

  const toggleWebsiteVisibility = async () => {
    if (isTogglingVisibility) return;
    setIsTogglingVisibility(true);
    setSaveStatus('saving');

    try {
      const newVisibility = !websiteVisibility;
      const response = await api.put(`/api/website/${tenantId}/visibility`, { isActive: newVisibility });
      if (response.data.success) {
        setWebsiteVisibility(newVisibility);
        setSaveStatus('saved');
      } else {
        throw new Error(response.data.message || 'Failed to update visibility');
      }
    } catch (error) {
      setSaveStatus('error');
      setError(error.response?.data?.message || 'Failed to update website visibility');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsTogglingVisibility(false);
      setTimeout(() => setSaveStatus(''), 1000);
    }
  };

  // FIXED: Accepts the payload data sent up right before running the final save database request
  const handleSave = async (section, payload = null) => {
    try {
      setSaveStatus('saving');
      
      // Use the incoming child component's clean local payload snapshot data directly
      const body = payload ?? websiteData[section];
      
      console.log(`[WebsiteManager] Executing backend save for section: ${section}`, body);
      
      const response = await api.put(
        `/api/website/${tenantId}/section/${section}`,
        body
      );

      if (response.data.success) {
        // Update parent state safely here only after a successful save action completes
        setWebsiteData(prev => ({ ...prev, [section]: body }));
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        throw new Error(response.data.message || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
      setError(error.response?.data?.message || error.message || `Failed to save ${section}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleCreateWebsite = async () => {
    try {
      setSaveStatus('saving');
      const response = await api.post(`/api/website/${tenantId}/create`);
      if (response.data.success) {
        setIsNewWebsite(false);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
        fetchWebsiteData();
      }
    } catch (error) {
      setSaveStatus('error');
      setError(error.message || 'Failed to create website');
      setTimeout(() => setError(null), 3000);
    }
  };

  // FIXED: Modified to securely merge updates cleanly without breaking child state isolated configurations
  const handleInputChange = useCallback((section, field, value, subSection = null) => {
    setWebsiteData(prev => {
      const updatedSection = { ...prev[section] };

      if (field === null) {
        return { ...prev, [section]: value };
      }

      if (subSection) {
        updatedSection[subSection] = {
          ...updatedSection[subSection],
          [field]: value
        };
      } else {
        updatedSection[field] = value;
      }

      return {
        ...prev,
        [section]: updatedSection
      };
    });
  }, []);

  const deleteWebsite = async () => {
    if (!window.confirm('Are you sure you want to delete all website configuration? This cannot be undone.')) {
      return;
    }
    try {
      setSaveStatus('saving');
      const response = await api.delete(`/api/website/${tenantId}`);
      if (response.data.success) {
        setWebsiteData(defaultWebsiteData);
        setIsNewWebsite(true);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      }
    } catch (error) {
      setSaveStatus('error');
      setError(error.message || 'Failed to delete website configuration');
      setTimeout(() => setError(null), 3000);
    }
  };

  const WebsiteManagerLayout = ({ children }) => (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2.5 py-1 rounded">
              Tenant Admin
            </span>
            <span className="text-slate-400 text-sm">/</span>
            <span className="text-sm font-semibold text-slate-900">Website Manager</span>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {children}
      </main>
    </div>
  );

  if (loading) {
    return (
      <WebsiteManagerLayout>
        <div className="flex flex-col justify-center items-center h-80 space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-600 border-t-transparent"></div>
          <p className="text-sm font-medium text-slate-500 animate-pulse">Loading dashboard info...</p>
        </div>
      </WebsiteManagerLayout>
    );
  }

  return (
    <WebsiteManagerLayout>
      <div>
        {/* Page Title & Status Panel */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8 pb-6 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Website Configuration</h1>
            <p className="mt-1 text-sm text-slate-500">Manage your appearance, core layout setups, page details, and active parameters.</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 bg-white p-2.5 rounded-xl border border-slate-200 shadow-xs self-start sm:self-auto">
            <div className="flex items-center gap-2 px-2">
              <span className={`h-2.5 w-2.5 rounded-full ${websiteVisibility ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
              <span className="text-xs font-semibold text-slate-600">
                {websiteVisibility ? 'Live on Web' : 'Hidden Offline'}
              </span>
            </div>

            <button 
              onClick={toggleWebsiteVisibility} 
              disabled={isTogglingVisibility} 
              className={`text-xs font-medium px-4 py-2 rounded-lg transition-all focus:ring-2 focus:ring-offset-1 focus:outline-hidden ${
                websiteVisibility 
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-400' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
              } ${isTogglingVisibility ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isTogglingVisibility ? 'Updating...' : (websiteVisibility ? 'Hide Website' : 'Publish Website')}
            </button>

            {saveStatus === 'saving' && (
              <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg font-medium animate-pulse">
                Saving...
              </span>
            )}
          </div>
        </div>
        
        {isNewWebsite ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs max-w-2xl mx-auto">
            <div className="px-6 py-12 md:py-16 text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100 mb-4">
                <FiGlobe className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">No Layout Configuration Active</h2>
              <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">
                You haven't set up a configuration map for this tenant yet. Initialize a fresh baseline to begin building pages.
              </p>
              <div className="mt-8">
                <button 
                  onClick={handleCreateWebsite} 
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-xl shadow-xs text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform active:scale-98 transition-all"
                >
                  Setup Configurations Layout
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
            
            <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-xs min-h-[300px]">
              {activeTab === 'branding' && (
                <BrandingSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  deleteWebsite={deleteWebsite}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'hero' && (
                <HeroSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'statistics' && (
                <StatisticsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'accreditations' && (
                <AccreditationsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'infrastructure' && (
                <InfrastructureSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'testimonials' && (
                <TestimonialsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'collaborations' && (
                <CollaborationsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'awards' && (
                <AwardsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'leadership' && (
                <LeadershipSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'recruiters' && (
                <RecruitersSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'events' && (
                <EventsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'news' && (
                <NewsSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'faq' && (
                <FAQSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'seo' && (
                <SEOSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'content' && (
                <ContentSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'gallery' && (
                <GallerySection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}

              {activeTab === 'faculty' && (
                <FacultySection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                  token={token}
                />
              )}

              {activeTab === 'courses' && (
                <CourseSection
                  tenantId={tenantId}
                  token={token}
                  handleInputChange={handleInputChange}
                />
              )}
              
              {activeTab === 'footer' && (
                <FooterSection
                  websiteData={websiteData}
                  handleInputChange={handleInputChange}
                  handleSave={handleSave}
                  tenantId={tenantId}
                />
              )}
            </div>
          </>
        )}

        <StatusNotification status={saveStatus} />
        
        {error && (
          <div className="fixed bottom-4 right-4 px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-xl shadow-lg flex items-center z-50">
            {error}
          </div>
        )}
      </div>
    </WebsiteManagerLayout>
  );
};

export default WebsiteManager;